import { useMutation } from '@tanstack/react-query';
import { Form, Space, TableProps } from 'antd';
import { DefaultOptionType } from 'antd/es/select';
import { CalculationSetupApis } from 'apis/CalculationSetupApis';
import { GroupRoleApis } from 'apis/GroupRoleApis';
import { EditIcon } from 'assets/svg';
import {
  ButtonAction,
  InputForm,
  LazySelectForm,
  SelectForm,
} from 'components';
import type {
  ILazySelectData,
  ILazySelectParams,
} from 'components/Select/LazySelectForm';
import InputNumberForm from 'components/Input/InputNumberForn';
import RenderMultiLine from 'components/RenderMultiLine/RenderMultiLine';
import TableComponent from 'components/Table/TableComponent';
import { useUser } from 'contexts/UserProvider';
import { ColumnsTypeCustom } from 'interfaces/Table/ColumnType';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import {
  bonusConditions,
  comparisonSignOptions,
  comparisonSigns,
  yesNoOptions,
} from 'utils/constants/constant';
import {
  formatDate,
  formatNumber_v2,
  generateUUID,
} from 'utils/constants/functions';
import { icons } from 'utils/constants/icons';
import { HocChangePagination } from 'utils/HocChangePagination';

interface IProps extends React.PropsWithChildren<TableProps> {
  jobTitles: Array<{
    id: string;
    jobName: string;
  }>;
  setJobTitles: (value: Array<{ id: string; jobName: string }>) => void;
  data: any;
  onSave: () => void;
  onCancel: () => void;
  onEdit: (data: any) => void;
  editIndex: number;
  // seteditIndex: (value: number) => void;
  isAdd: boolean;
  form: any;
  refetch: (
    page?: number,
    page_size?: number,
    isExport?: boolean,
    headerRow?: any,
  ) => void;
  isFirstRenderEdit: any;
}

const defaultTypeOptions: DefaultOptionType[] = Object.keys(bonusConditions)
  .filter((item) => !['LGTD'].includes(item))
  .map((item) => ({
    label: bonusConditions[item as keyof typeof bonusConditions],
    value: item,
  }));

const hasParentTypeOptions: DefaultOptionType[] = Object.keys(bonusConditions)
  .filter((item) => !['DTTD', 'LGTD'].includes(item))
  .map((item) => ({
    label: bonusConditions[item as keyof typeof bonusConditions],
    value: item,
  }));

const { SaveTwoTone, CloseCircleTwoTone } = icons;
const TableRewardConditionTab = forwardRef((props: IProps, ref: any) => {
  const {
    data = {},
    loading,
    refetch,
    onSave,
    onCancel,
    onEdit,
    editIndex,
    isAdd,
    form,
    jobTitles,
    setJobTitles,
    isFirstRenderEdit,
  } = props;

  const { user } = useUser();
  const [typeOptions, setTypeOptions] =
    useState<DefaultOptionType[]>(defaultTypeOptions);
  const [rewardSetupOptions, setRewardSetupOptions] = useState<
    DefaultOptionType[]
  >([]);
  const [groupRoleManagementOptions, setGroupRoleManagementOptions] = useState<
    DefaultOptionType[]
  >([]);
  const [hasParentCondition, setHasParentCondition] = useState<boolean>(false);
  const [isMaxCondition, setIsMaxCondition] = useState<boolean>(false);

  // State for LazySelectForm - Điều kiện cha
  const [rewardSetupData, setRewardSetupData] = useState<ILazySelectData>({
    options: [],
    totalItems: 0,
  });
  const [rewardSetupHasNextPage, setRewardSetupHasNextPage] = useState(true);

  const groupRoleManagementOptionsProcessed = useMemo(() => {
    const rewardIds = [
      ...new Set(
        (data?.items || [])
          .map((item: any) => (item.groupRoleManagement || '').split(';'))
          .flat(),
      ),
    ];

    const filtered = groupRoleManagementOptions.filter(
      (item) =>
        !rewardIds.includes(item.id) ||
        (editIndex >= 0 &&
          (data.items[editIndex].groupRoleManagement || '').includes(item.id)),
    );

    return filtered;
  }, [data, groupRoleManagementOptions, editIndex]);

  const rewardSetupOptionsProcess = useMemo(() => {
    if (editIndex >= 0) {
      const id = data.items[editIndex]?.id;
      const isParent = data.items.find((item: any) => item.mainSetupId === id);

      if (isParent) return [];
    }

    if (isAdd || editIndex === -1) return rewardSetupOptions;

    const item = data.items[editIndex];

    return rewardSetupOptions.filter((option) => option.value !== item.id);
  }, [data, editIndex, isAdd, rewardSetupOptions]);

  const handleChangeParent = useCallback(
    (value: string) => {
      if (
        isFirstRenderEdit.current &&
        editIndex >= 0 &&
        !data.items[editIndex].mainSetupId
      ) {
        setHasParentCondition(false);
        setTypeOptions(defaultTypeOptions);
        isFirstRenderEdit.current = false;
        return;
      }
      const item = data.items.find((item: any) => item.id === value);

      const values: any = {
        type: undefined,
      };

      if (value) {
        setTypeOptions(hasParentTypeOptions);

        if (item) {
          const ids = (item.groupRoleId ?? '').split(';');
          const names = (item.groupRoleName ?? '').split(';');

          values.groupRoleManagement = (item.groupRoleManagement ?? '').split(
            ';',
          );

          const jobTitles = ids.map((item: string, index: number) => ({
            id: item,
            jobName: names[index],
          }));

          setJobTitles(jobTitles);
        }
      } else {
        setTypeOptions(defaultTypeOptions);
        setJobTitles([]);
        values.groupRoleManagement = [];
      }

      form.setFieldsValue(values);

      setHasParentCondition(Boolean(value));
      isFirstRenderEdit.current = false;
    },
    [data.items, editIndex, form, isFirstRenderEdit, setJobTitles],
  );

  const handleChangeGroupRoleManagement = useCallback(
    (value: string) => {
      if (!value) {
        setJobTitles([]);
        return;
      }

      const jobTitles = groupRoleManagementOptions
        .filter((item: any) => value.includes(item.id))
        .map((item: any) => ({
          id: item.tjtobTitle?.id,
          jobName: item.tjtobTitle?.jobName,
        }));

      setJobTitles(jobTitles);
    },
    [groupRoleManagementOptions, setJobTitles],
  );

  const {
    mutate: getComboboxRewardSetup,
    isPending: isPendingGetComboboxRewardSetup,
  } = useMutation({
    mutationFn: CalculationSetupApis.getComboboxRewardSetup,
    onSuccess: (data) => {
      const options = (data?.data?.getRewardSetup?.items || [])
        .filter((item: any) => !item.mainSetupId)
        .map((item: any) => ({
          label: item?.id,
          value: item?.id,
        }));

      setRewardSetupOptions(options);
    },
  });

  // Handler for LazySelectForm - Điều kiện cha
  const loadRewardSetupData = useCallback(
    async (params: { skip: number; take: number; search?: string }) => {
      try {
        const response = await CalculationSetupApis.getComboboxRewardSetup({
          skip: params.skip,
          take: params.take,
          type: 'DTTD',
          ...(params.search && { id: params.search }),
        });

        const newOptions = (response?.data?.getRewardSetup?.items || [])
          .filter((item: any) => {
            const isNotSelf = isAdd
              ? true
              : item.id !== data.items[editIndex]?.id;
            return !item.mainSetupId && isNotSelf;
          })
          .map((item: any) => ({
            label: item?.id,
            value: item?.id,
          }));

        setRewardSetupData((prev) => ({
          options:
            params.skip === 0 ? newOptions : [...prev.options, ...newOptions],
          totalItems: response?.data?.getRewardSetup?.pageInfo?.totalItems || 0,
        }));

        setRewardSetupHasNextPage(newOptions.length === params.take);
      } catch (error) {
        console.error('Error loading reward setup data:', error);
      }
    },
    [data.items, editIndex, isAdd],
  );

  const handleRewardSetupScrollChange = useCallback(
    (params: ILazySelectParams) => {
      const skip = params.value
        ? 0
        : (rewardSetupData.options.length / params.pageSize) * params.pageSize;
      loadRewardSetupData({
        skip,
        take: params.pageSize,
        ...(params.value && { search: params.value }),
      });
    },
    [loadRewardSetupData, rewardSetupData.options.length],
  );

  const {
    mutate: getComboboxGroupRoleManagement,
    isPending: isPendingGetComboboxGroupRoleManagement,
  } = useMutation({
    mutationFn: GroupRoleApis.getComboboxJobTitleNew,
    onSuccess: (data) => {
      const rewardIds: string[] = [];
      const options = (data?.data?.getGroupRoleManagement?.items || [])
        .filter((item: any) => {
          if (rewardIds.includes(item.rewardId)) return false;
          rewardIds.push(item.rewardId);
          return true;
        })
        .map((item: any) => ({
          ...item,
          id: item.rewardId,
          tjtobTitle: {
            id: item.jobTitle,
            jobName: item.jobTitleName,
          },
        }));

      setGroupRoleManagementOptions(options);
    },
  });

  const handleChangeType = (value: string) => {
    if (value === 'DTTD') {
      form.setFieldsValue({
        maxCondition: undefined,
      });
    }
    setIsMaxCondition(value === 'DTTD');
  };

  const columns: ColumnsTypeCustom = [
    {
      title: 'STT',
      width: 50,
      align: 'center',
      render: (_, __, index) =>
        data.pageInfo?.itemsPerPage * (data.pageInfo?.currentPage - 1) +
        index +
        1,
    },
    {
      title: 'Mã điều kiện',
      width: 130,
      dataIndex: 'id',
      render: (value, record, index) => {
        const isChange = isAdd && index === 0;

        if (isChange)
          return (
            <Form.Item name={'id'} className="m-0">
              <InputForm
                placeholder="Nhập mã điều kiện"
                size="middle"
                // maxLength={6}
              />
            </Form.Item>
          );

        return value;
      },
    },
    {
      title: 'Điều kiện cha',
      width: 130,
      dataIndex: 'mainSetupId',
      render: (value, record, index) => {
        const isChange = (isAdd && index === 0) || editIndex === index;

        if (isChange)
          return (
            <Form.Item name={'mainSetupId'} className="m-0">
              <LazySelectForm
                data={rewardSetupData}
                hasNextPage={rewardSetupHasNextPage}
                placeholder="Chọn điều kiện cha"
                size="middle"
                onChange={handleChangeParent}
                loading={isPendingGetComboboxRewardSetup}
                onSelectScrollChange={handleRewardSetupScrollChange}
                showSearch
                customeSize={20}
              />
            </Form.Item>
          );

        return value;
      },
    },
    {
      title: 'Loại',
      width: 200,
      dataIndex: ['ttype', 'name'],
      render: (value, record, index) => {
        const isChange = (isAdd && index === 0) || editIndex === index;

        if (isChange)
          return (
            <Form.Item name={'type'} className="m-0">
              <SelectForm
                options={typeOptions}
                placeholder="Chọn loại"
                size="middle"
                onChange={handleChangeType}
              />
            </Form.Item>
          );

        return value;
      },
    },
    {
      title: 'Hỗ trợ',
      width: 130,
      dataIndex: 'supportYn',
      render: (value, record, index) => {
        const isChange = (isAdd && index === 0) || editIndex === index;

        if (isChange)
          return (
            <Form.Item name={'supportYn'} className="m-0">
              <SelectForm
                options={yesNoOptions}
                placeholder="Chọn hỗ trợ"
                size="middle"
              />
            </Form.Item>
          );

        return value ? 'Y' : 'N';
      },
    },
    {
      title: 'Mã thưởng DMS',
      width: 200,
      dataIndex: 'groupRoleManagement',
      render: (value, record, index) => {
        const isChange = (isAdd && index === 0) || editIndex === index;

        if (isChange)
          // k hiển thị những mã đa khai báo;  cho phép chọn n
          return (
            <Form.Item name={'groupRoleManagement'} className="m-0">
              <SelectForm
                disabled={hasParentCondition}
                // fieldNames={{
                //   label: 'rewardId',
                //   value: 'id',
                // }}
                placeholder="Chọn mã thưởng DMS"
                size="middle"
                mode="multiple"
                showSearch
                onChange={handleChangeGroupRoleManagement}
                loading={isPendingGetComboboxGroupRoleManagement}
                options={groupRoleManagementOptionsProcessed.map((item) => ({
                  label: item.rewardId,
                  value: item.id,
                }))}
              />
            </Form.Item>
          );

        return value ? value?.split(';').join('; ') : '-';
      },
    },
    {
      title: 'Mã chức vụ',
      width: 90,
      dataIndex: 'groupRoleId',
      render: (value, record, index) => {
        const isChange = (isAdd && index === 0) || editIndex === index;
        const data = isChange
          ? jobTitles.map((item) => item.id).join(';')
          : value;

        return <RenderMultiLine data={data} />;
      },
    },
    {
      title: 'Tên chức vụ',
      width: 330,
      dataIndex: 'groupRoleName',
      render: (value, record, index) => {
        const isChange = (isAdd && index === 0) || editIndex === index;
        const data = isChange
          ? jobTitles.map((item) => item.jobName).join(';')
          : value;

        return <RenderMultiLine data={data} />;
      },
    },
    {
      title: 'Thực hiện (dấu so sánh)',
      width: 180,
      dataIndex: 'condition',
      render: (value, record, index) => {
        const isChange = editIndex === index || (isAdd && index === 0);

        if (isChange)
          return (
            <Form.Item name={'condition'} className="m-0">
              <SelectForm
                options={comparisonSignOptions}
                placeholder="Chọn dấu so sánh"
                size="middle"
                allowClear={false}
              />
            </Form.Item>
          );

        return comparisonSigns[value as keyof typeof comparisonSigns];
      },
    },
    {
      title: 'Thực hiện',
      width: 130,
      dataIndex: 'value',
      render: (value, record, index) => {
        const isChange = editIndex === index || (isAdd && index === 0);

        if (isChange)
          return (
            <Form.Item name={'value'} className="m-0">
              <InputNumberForm
                min={1}
                max={999}
                placeholder="Nhập giá trị"
                size="middle"
              />
            </Form.Item>
          );

        return value ? formatNumber_v2(value) : '-';
      },
    },
    {
      title: 'Tối đa khung thưởng',
      width: 180,
      dataIndex: 'maxCondition',
      render: (value, record, index) => {
        const isChange = editIndex === index || (isAdd && index === 0);

        if (isChange)
          return (
            <Form.Item name={'maxCondition'} className="m-0">
              <InputNumberForm
                disabled={isMaxCondition}
                readOnly={isMaxCondition}
                min={1}
                max={999}
                placeholder="Nhập giá trị"
                size="middle"
              />
            </Form.Item>
          );

        return value;
      },
    },
    {
      title: 'Giá trị',
      width: 130,
      dataIndex: 'valueType',
      render: (value, record, index) => {
        const isChange = editIndex === index || (isAdd && index === 0);

        if (isChange) return '%';

        return value;
      },
    },
    {
      title: 'Ghi chú',
      width: 250,
      dataIndex: 'note',
      render: (value, record, index) => {
        const isChange = (isAdd && index === 0) || editIndex === index;

        if (isChange)
          return (
            <Form.Item name={'note'} className="m-0">
              <InputForm
                placeholder="Nhập ghi chú"
                size="middle"
                maxLength={500}
              />
            </Form.Item>
          );

        return value;
      },
    },
    {
      title: 'Người khai báo',
      width: 130,
      dataIndex: ['tcreateUser', 'fullName'],
    },
    {
      title: 'Ngày khai báo',
      width: 170,
      type: 'date',
      dataIndex: 'createDate',
      render: (value) => formatDate(value),
    },
    {
      title: 'Người cập nhật',
      width: 130,
      dataIndex: ['tmodifyUser', 'fullName'],
    },
    {
      title: 'Ngày cập nhật',
      width: 170,
      type: 'date',
      dataIndex: 'modifyDate',
      render: (value) => formatDate(value),
    },
    {
      fixed: 'right',
      align: 'center',
      width: 70,
      render: (__, record, index) =>
        index === editIndex || (isAdd && index === 0) ? (
          <Space size={'middle'} className="text-lg">
            <ButtonAction tooltip="Lưu" onClick={onSave}>
              <SaveTwoTone style={{ fontSize: 20 }} />
            </ButtonAction>
            <ButtonAction tooltip="Hủy" onClick={onCancel}>
              <CloseCircleTwoTone style={{ fontSize: 20 }} />
            </ButtonAction>
          </Space>
        ) : (
          <Space size={'middle'} className="text-lg">
            <ButtonAction
              tooltip="Chỉnh sửa"
              onClick={() => onEdit(index)}
              disabled={!(user?.isAdmin || user?.isHO || user?.isNS)}
            >
              <EditIcon />
            </ButtonAction>
          </Space>
        ),
    },
  ];

  useImperativeHandle(ref, () => ({
    exportExcel,
  }));

  const exportExcel = () => {
    refetch(1, data.pageInfo?.totalItems, true, columns);
  };

  useEffect(() => {
    // Load initial data for LazySelectForm
    loadRewardSetupData({ skip: 0, take: 20 });
  }, [loadRewardSetupData, isAdd, editIndex]);

  useEffect(() => {
    getComboboxGroupRoleManagement({
      skip: 0,
      take: 100,
    });
  }, [getComboboxGroupRoleManagement, isAdd]);

  useEffect(() => {
    setJobTitles([]);
  }, [isAdd]);

  useEffect(() => {
    if (isAdd) return handleChangeParent('');

    if (editIndex === -1) return;
    const item = data.items[editIndex];

    handleChangeParent(item.mainSetupId);
  }, [editIndex, handleChangeParent, data, isAdd]);

  useEffect(() => {
    if (editIndex === -1) return;
    const item = data.items[editIndex];
    form.setFieldsValue({
      type: item.type,
    });
  }, [editIndex, form, data]);

  return (
    <Form form={form}>
      <TableComponent
        scroll={{ x: 'max-content' }}
        columns={columns}
        // dataSource={[{}]}
        dataSource={data.items}
        rowKey={(record: any) => record?.id || generateUUID()}
        loading={loading}
        pagination={{
          current: data.pageInfo?.currentPage,
          pageSize: data.pageInfo?.itemsPerPage,
          total: data.pageInfo?.totalItems,
          onChange: (page, page_size) => {
            refetch(page, page_size);
          },
        }}
        // pagination={false}
      />
    </Form>
  );
});

export default TableRewardConditionTab;
